import { PreviousButton } from '../table-session/PreviousButton';
import { renderCallTokens } from '../../utils/callTokensInText';

interface ConceptualQuestionPanelProps {
  question: string | null;
  onPrevious: () => void;
  onNext: () => void;
  atFirst: boolean;
  atLast: boolean;
}

/**
 * The frame's right-hand panel, where a practice session puts its bid pad (feature 029).
 *
 * <p>Nothing is being answered here, so the pad is replaced by the thing the pad would have been
 * answering: the question itself, in full. On the practice pages the question is the fixed
 * "What is your call?" that the panel heading already carries; a conceptual question runs to 85
 * characters and is the substance of the problem, so it wants a column of its own rather than a
 * heading. The answer follows in the row beneath (see ConceptualAnswerPanel).</p>
 *
 * <p><code>call-stack</code> and <code>verdict-actions</code> are the CSS hooks the height-starved
 * viewports tighten this column by (index.css). They are load-bearing on a landscape phone, where
 * the whole panel row is ~130px tall — hence the same class names the practice pages use, not
 * look-alikes.</p>
 */
export function ConceptualQuestionPanel({
  question,
  onPrevious,
  onNext,
  atFirst,
  atLast,
}: ConceptualQuestionPanelProps) {
  return (
    <div className="call-stack space-y-3">
      {/* Free-wrapping prose, and `break-words` so an unbroken token cannot widen the panel past
          the phone. The call tokens inside it become bridge notation (FR-022). */}
      <p
        className="text-[15px] leading-[1.55] text-ink break-words"
        data-testid="conceptual-question"
      >
        {question ? renderCallTokens(question) : <span className="text-ink-soft">No question recorded.</span>}
      </p>

      <nav className="verdict-actions flex flex-wrap items-center gap-2" aria-label="Problem navigation">
        <PreviousButton onClick={onPrevious} disabled={atFirst} testId="conceptual-prev" />
        <button
          type="button"
          onClick={onNext}
          disabled={atLast}
          data-testid="conceptual-next"
          className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </nav>
    </div>
  );
}
